import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, MapPin, Users, Maximize2, Trophy, Lightbulb, Clock,
  Heart, Share2, UserPlus, UserRound, UsersRound, Mic, Image as ImageIcon, Camera, Film, Video,
  X, ChevronLeft, ChevronRight, Plus, UploadCloud, Search, Lock, Globe,
} from 'lucide-react';
import HubLayout from '../common/HubLayout';
import { lifeEventAPI } from '../../config/lifeEventAPI';
import { getMediaServeUrl } from '../../config/mediaAPI';
import { getUser } from '../../utils/secureStorage';
import { getCategoryConfig } from '../../utils/mediaLayout';
import { getStoryQuestions, askQuestion } from '../../services/curiosityAPI';
import { getStoryEngagement, toggleLove } from '../../services/reactionService';
import { contributeMedia } from '../../services/mediaService';
import { axiosInstance } from '../../services/api';
import MediaCapture from '../MediaCapture';
import VideoProcessingBadge from '../videoactivation/VideoProcessingBadge';
import StoryProcessingBanner from '../videoactivation/StoryProcessingBanner';
import VideoTranscript from './VideoTranscript';
import SpeakerConfirmationInline from '../videoactivation/SpeakerConfirmationInline';
import WisdomMomentsList from '../videoactivation/WisdomMomentsList';
import { getPendingConfirmations, searchPeople } from '../../services/videoActivationAPI';
import { gradeForYear, currentSchoolYear, gradeLabel as gradeLabelUtil } from '../../utils/schoolYears';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const FONT_NOTE = "'Caveat', 'Brush Script MT', cursive";
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

function getImgUrl(media) {
  if (media.file_url && media.file_url.startsWith('http')) return media.file_url;
  if (media.url && media.url.startsWith('http')) return media.url;
  if (media.url && media.url.startsWith('/')) return `${API_ORIGIN}${media.url}`;
  if (media.file && typeof media.file === 'string' && media.file.startsWith('http')) return media.file;
  if (media.id) return getMediaServeUrl(media.id);
  return '';
}

/* ─── Lightbox ─── */
function Lightbox({ photos, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const photo = photos[idx];
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx(i => (i - 1 + photos.length) % photos.length);
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % photos.length);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [photos.length, onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(18,20,14,.95)', zIndex: 2000,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }} role="dialog" aria-modal="true">
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,.1)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={24} /></button>
      {photos.length > 1 && <>
        <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); }} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,.1)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={28} /></button>
        <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % photos.length); }} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,.1)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={28} /></button>
      </>}
      <img src={photo ? getImgUrl(photo) : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'} alt={photo?.caption || ''} onClick={e => e.stopPropagation()} style={{ maxWidth: '84vw', maxHeight: '76vh', objectFit: 'contain', borderRadius: 4 }} />
      {photo?.caption && <p style={{ fontFamily: FONT_H, fontSize: 19, color: '#fff', marginTop: 14, textAlign: 'center' }}>{photo.caption}</p>}
      <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14, color: 'rgba(255,255,255,.6)', marginTop: 10 }}>{idx + 1} / {photos.length}</p>
    </div>
  );
}

/* ─── Media Uploader (matches TellAStory pattern) ─── */
function MediaUploader({ storyId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  const [isMediaCaptureOpen, setIsMediaCaptureOpen] = useState(false);

  const handleMediaCaptured = (mediaData) => {
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file: mediaData.file, preview: mediaData.preview,
      name: mediaData.file.name, size: mediaData.file.size,
      type: mediaData.file.type, mediaType: mediaData.type,
      duration: mediaData.duration,
    };
    setLocalFiles(prev => [...prev, localFile]);
    setIsMediaCaptureOpen(false);
  };

  const handleFileUpload = (file, mediaType) => {
    const previewUrl = URL.createObjectURL(file);
    setLocalFiles(prev => [...prev, {
      id: `local-${Date.now()}-${Math.random()}`,
      file, preview: previewUrl, name: file.name, size: file.size,
      type: file.type, mediaType,
    }]);
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif';
    input.multiple = true;
    input.onchange = (e) => Array.from(e.target.files).forEach(f => {
      if (!f.type.startsWith('image/')) return;
      if (f.size > 10 * 1024 * 1024) { alert(`"${f.name}" is too large (${(f.size / (1024*1024)).toFixed(1)} MB). Images must be under 10 MB.`); return; }
      handleFileUpload(f, 'image');
    });
    input.click();
  };

  const handleVideoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/avi,video/quicktime,video/wmv,video/webm,.mov';
    input.multiple = true;
    input.onchange = (e) => Array.from(e.target.files).forEach(f => {
      if (!f.type.startsWith('video/')) return;
      if (f.size > 500 * 1024 * 1024) { alert(`"${f.name}" is too large (${(f.size / (1024*1024)).toFixed(0)} MB). Videos must be under 500 MB.`); return; }
      handleFileUpload(f, 'video');
    });
    input.click();
  };

  const handleAudioUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg';
    input.multiple = true;
    input.onchange = (e) => Array.from(e.target.files).forEach(f => {
      if (!f.type.startsWith('audio/')) return;
      if (f.size > 50 * 1024 * 1024) { alert(`"${f.name}" is too large (${(f.size / (1024*1024)).toFixed(1)} MB). Audio files must be under 50 MB.`); return; }
      handleFileUpload(f, 'audio');
    });
    input.click();
  };

  const removeFile = (fileId) => {
    setLocalFiles(prev => {
      const f = prev.find(x => x.id === fileId);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter(x => x.id !== fileId);
    });
  };

  const handleSubmit = async () => {
    if (localFiles.length === 0 || uploading) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const files = localFiles.map(f => f.file);
      await contributeMedia(storyId, files, { onProgress: (pct) => setUploadProgress(pct) });
      localFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      setLocalFiles([]);
      if (onUploaded) onUploaded();
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  return (
    <div className="bg-white border border-gray-300 rounded-2xl p-5 sm:p-6 mb-5">
      <h2 className="font-serif text-2xl font-semibold text-gray-800 mb-1">Add a photo or recording</h2>
      <p className="text-base text-gray-500 mb-5">Optional — but a photo or voice note brings the story to life</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button type="button" onClick={() => setIsMediaCaptureOpen(true)}
          className="p-4 rounded-xl border-2 border-gray-300 hover:border-green-500 hover:shadow-lg bg-white transition-all group">
          <div className="flex flex-col items-center">
            <div className="relative mb-2">
              <Video className="w-8 h-8 text-green-600 group-hover:text-green-700" />
              <Mic className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1" />
            </div>
            <div className="font-semibold text-sm text-gray-800">Record</div>
            <div className="text-xs text-gray-500">Audio/Video</div>
          </div>
        </button>
        <button type="button" onClick={handleImageUpload}
          className="p-4 rounded-xl border-2 border-gray-300 hover:border-blue-500 hover:shadow-lg bg-white transition-all group">
          <div className="flex flex-col items-center">
            <Camera className="w-10 h-10 text-blue-600 group-hover:text-blue-700 mb-2" />
            <div className="font-semibold text-sm text-gray-800">Add Photos</div>
            <div className="text-xs text-gray-500">Upload Images</div>
          </div>
        </button>
        <button type="button" onClick={handleVideoUpload}
          className="p-4 rounded-xl border-2 border-gray-300 hover:border-purple-500 hover:shadow-lg bg-white transition-all group">
          <div className="flex flex-col items-center">
            <Film className="w-10 h-10 text-purple-600 group-hover:text-purple-700 mb-2" />
            <div className="font-semibold text-sm text-gray-800">Add Videos</div>
            <div className="text-xs text-gray-500">Upload Files</div>
          </div>
        </button>
        <button type="button" onClick={handleAudioUpload}
          className="p-4 rounded-xl border-2 border-gray-300 hover:border-orange-500 hover:shadow-lg bg-white transition-all group">
          <div className="flex flex-col items-center">
            <Mic className="w-10 h-10 text-orange-600 group-hover:text-orange-700 mb-2" />
            <div className="font-semibold text-sm text-gray-800">Add Audio</div>
            <div className="text-xs text-gray-500">Upload Files</div>
          </div>
        </button>
      </div>

      {/* Drag & Drop */}
      <div
        className={`p-6 border-2 border-dashed rounded-xl transition-all ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={e => { e.preventDefault(); setIsDragOver(false); }}
        onDrop={e => { e.preventDefault(); setIsDragOver(false); Array.from(e.dataTransfer.files).forEach(f => { const mt = f.type.startsWith('video/') ? 'video' : f.type.startsWith('audio/') ? 'audio' : 'image'; const maxMB = mt === 'video' ? 500 : mt === 'audio' ? 50 : 10; if (f.size > maxMB * 1024 * 1024) { alert(`"${f.name}" is too large (${(f.size/(1024*1024)).toFixed(0)} MB). ${mt === 'video' ? 'Videos' : mt === 'audio' ? 'Audio files' : 'Images'} must be under ${maxMB} MB.`); return; } handleFileUpload(f, mt); }); }}
      >
        <div className="text-center">
          <Camera className={`w-10 h-10 mx-auto mb-2 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className={`text-sm font-medium ${isDragOver ? 'text-blue-700' : 'text-gray-600'}`}>
            {isDragOver ? 'Drop Photos Here!' : 'Drag & Drop Photos Here'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Or use the buttons above</p>
        </div>
      </div>

      {/* Preview + Submit */}
      {localFiles.length > 0 && (
        <div className="mt-5">
          <div className="text-sm font-medium text-gray-800 mb-3">Media Files ({localFiles.length}):</div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {localFiles.map(media => (
              <div key={media.id} className="relative group">
                {media.mediaType === 'image' ? (
                  <img src={media.preview} alt={media.name} className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                ) : (
                  <div className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-xs text-gray-500">
                    {media.mediaType === 'video' ? '🎥' : '🎤'} {media.name?.slice(0, 12)}
                  </div>
                )}
                <button onClick={() => removeFile(media.id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
              </div>
            ))}
          </div>
          {uploading && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ background: '#EFEEE5', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 999,
                  background: uploadProgress < 100 ? 'linear-gradient(90deg, #6B8E23, #556B2F)' : '#2F7A3A',
                  width: `${uploadProgress}%`, transition: 'width 0.3s',
                }} />
              </div>
              <p style={{ textAlign: 'center', fontSize: 13, color: '#556B2F', fontWeight: 600, marginTop: 4 }}>
                {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}
              </p>
            </div>
          )}
          <button onClick={handleSubmit} disabled={uploading}
            className="w-full py-3 rounded-xl font-bold text-white transition-colors"
            style={{ background: uploading ? '#bbb' : '#556B2F' }}
          >
            {uploading ? `Uploading... ${uploadProgress}%` : `Add ${localFiles.length} file${localFiles.length > 1 ? 's' : ''} to this memory`}
          </button>
        </div>
      )}

      {/* MediaCapture Modal (same as TellAStory) */}
      <MediaCapture
        isOpen={isMediaCaptureOpen}
        onClose={() => setIsMediaCaptureOpen(false)}
        onMediaCaptured={handleMediaCaptured}
      />
    </div>
  );
}

/* ─── Conversation Rail ─── */
function ConversationRail({ storyId, authorName, engagement, notes, onRefresh }) {
  const [noteText, setNoteText] = useState('');
  const [sending, setSending] = useState(false);
  const love = engagement?.love || { count: 0, viewer_sent: false, members: [] };
  const engagementNotes = engagement?.notes || [];
  const FEELINGS = [
    { emoji: '🥰', text: 'This made my day' },
    { emoji: '🙏', text: 'Grateful for you' },
    { emoji: '😂', text: 'Made me laugh' },
  ];

  const handleSendNote = async () => {
    if (!noteText.trim() || sending) return;
    setSending(true);
    try {
      await askQuestion(storyId, { question_text: noteText.trim(), question_type: 'general' });
      setNoteText('');
      if (onRefresh) onRefresh();
    } catch (err) { console.error('Send note error:', err); }
    finally { setSending(false); }
  };

  const handleToggleLove = async () => {
    try { await toggleLove('story', storyId); if (onRefresh) onRefresh(); }
    catch (err) { console.error('Love toggle error:', err); }
  };

  return (
    <div>
      {/* Love banner */}
      <div style={{
        background: 'linear-gradient(135deg, #FBECE4, #FCF3EC)', border: '1px solid #F3D9CC',
        borderRadius: 16, padding: '18px 20px', marginBottom: 20,
      }}>
        {love.count > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ display: 'flex', flexShrink: 0 }}>
              {love.members.slice(0, 3).map((m, i) => (
                <div key={m.id} style={{ width: 36, height: 36, borderRadius: '50%', background: m.color || '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', marginLeft: i > 0 ? -10 : 0 }}>{m.initial}</div>
              ))}
            </div>
            <p style={{ flex: 1, fontSize: 15, color: '#7a4a3a', margin: 0, fontFamily: FONT_B }}>
              <b style={{ color: '#C0432C' }}>
                {love.viewer_sent ? (love.count === 1 ? 'You' : `You and ${love.count - 1} other${love.count > 2 ? 's' : ''}`) : `${love.count} family member${love.count !== 1 ? 's' : ''}`}
              </b> loved this.
            </p>
          </div>
        )}
        <button onClick={handleToggleLove} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
          background: love.viewer_sent ? '#FBECE4' : '#fff', color: '#C0432C', fontWeight: 700, fontSize: 15,
          padding: '12px 0', borderRadius: 999, border: '1px solid #F0C7B5', cursor: 'pointer', minHeight: 48, fontFamily: FONT_B,
        }}>
          <Heart size={18} fill={love.viewer_sent ? '#C0432C' : 'none'} />
          {love.viewer_sent ? 'You sent love' : 'Send love'}
        </button>
      </div>

      {/* Conversation card */}
      <div style={{ background: '#fff', border: '1px solid #E6E4D8', borderRadius: 16, padding: '20px', marginBottom: 20 }}>
        <h3 style={{ fontFamily: FONT_H, fontWeight: 600, fontSize: 22, color: '#2C2F26', margin: '0 0 4px' }}>
          Leave a note for {authorName}
        </h3>
        <p style={{ fontSize: 14, color: '#828A76', margin: '0 0 16px', fontFamily: FONT_B }}>
          A keepsake they'll treasure — not a comment.
        </p>

        {/* Composer */}
        <div style={{ background: '#FFFDF6', border: '1.5px dashed #E4D9B8', borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder={`Write something ${authorName} will keep…`}
            style={{ width: '100%', border: 'none', background: 'transparent', resize: 'none', fontFamily: FONT_NOTE, fontSize: 21, color: '#3a3f31', lineHeight: 1.3, minHeight: 60, outline: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 10 }}>
            <button onClick={handleSendNote} disabled={!noteText.trim() || sending} style={{
              background: noteText.trim() && !sending ? '#556B2F' : '#bbb', color: '#fff', fontWeight: 700, fontSize: 13,
              padding: '10px 22px', borderRadius: 999, border: 'none', cursor: noteText.trim() ? 'pointer' : 'not-allowed', fontFamily: FONT_B,
            }}>{sending ? 'Sending...' : 'Send note'}</button>
          </div>
        </div>

        {/* Feeling starters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {FEELINGS.map(f => (
            <button key={f.text} onClick={() => setNoteText(f.text)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', color: '#2C2F26', fontWeight: 500, fontSize: 14,
              padding: '8px 14px', borderRadius: 999, border: '1px solid #E6E4D8', cursor: 'pointer', fontFamily: FONT_B,
            }}>{f.emoji} {f.text}</button>
          ))}
        </div>

        {/* Notes list */}
        {(engagementNotes.length > 0 || (notes && notes.length > 0)) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {engagementNotes.map((n, i) => (
              <div key={n.id || `en-${i}`} style={{ background: '#FFFDF6', border: '1px solid #F0E9D4', borderRadius: 12, padding: '14px 16px' }}>
                <p style={{ fontFamily: FONT_NOTE, fontSize: 22, lineHeight: 1.3, color: '#3a3f31', margin: '0 0 8px' }}>{n.note}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: n.author?.color || '#556B2F', color: '#fff', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n.author?.initial || '?'}</div>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: '#2C2F26', fontFamily: FONT_B }}>{n.author?.name || 'Family member'}</span>
                  {n.author?.relationship && <span style={{ fontSize: 12, color: '#828A76' }}>· {n.author.relationship}</span>}
                </div>
              </div>
            ))}
            {(notes || []).map((n, i) => (
              <div key={n.id || `q-${i}`} style={{ background: '#FFFDF6', border: '1px solid #F0E9D4', borderRadius: 12, padding: '14px 16px' }}>
                <p style={{ fontFamily: FONT_NOTE, fontSize: 22, lineHeight: 1.3, color: '#3a3f31', margin: '0 0 8px' }}>{n.question_text || ''}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: n.asker?.avatarColor || '#556B2F', color: '#fff', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(n.asker?.firstName || '?').charAt(0)}</div>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: '#2C2F26', fontFamily: FONT_B }}>{`${n.asker?.firstName || ''} ${n.asker?.lastName || ''}`.trim() || 'Family member'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const AVATAR_COLORS_PP = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
const ppAvatarColor = (name) => AVATAR_COLORS_PP[name ? name.charCodeAt(0) % AVATAR_COLORS_PP.length : 0];

/* ─── People in this Memory ─── */
function PeopleInMemory({ storyId, people, onUpdate }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const timer = setTimeout(() => {
      searchPeople(tagQuery).then(data => {
        const taggedIds = new Set((people || []).map(p => p.id));
        setResults((data.people || []).filter(p => !taggedIds.has(p.id)));
      }).catch(() => {});
    }, tagQuery ? 250 : 0);
    return () => clearTimeout(timer);
  }, [pickerOpen, tagQuery, people]);

  useEffect(() => { if (pickerOpen && inputRef.current) inputRef.current.focus(); }, [pickerOpen]);

  const handleTag = async (personId) => {
    try {
      await axiosInstance.post(`/lifeevent/events/${storyId}/tag_person/`, { person_id: personId });
      setPickerOpen(false);
      if (onUpdate) onUpdate();
    } catch (e) { console.error('Tag failed:', e); }
  };

  const handleUntag = async (personId) => {
    try {
      await axiosInstance.post(`/lifeevent/events/${storyId}/untag_person/`, { person_id: personId });
      if (onUpdate) onUpdate();
    } catch (e) { console.error('Untag failed:', e); }
  };

  return (
    <div style={{
      maxWidth: 900, margin: '0 auto 30px', background: '#fff', border: '1px solid #EAE8DB',
      borderRadius: 16, padding: '18px 22px', boxShadow: '0 6px 20px rgba(40,46,28,.06)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <UsersRound size={18} color="#556B2F" />
        <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26' }}>People in this memory</span>
        <span style={{ background: '#F0F5E8', color: '#556B2F', fontWeight: 700, fontSize: 13, padding: '2px 10px', borderRadius: 999, fontFamily: FONT_B }}>{(people || []).length}</span>
        <span style={{ fontSize: 13.5, color: '#9AA088', fontFamily: FONT_B }}>Tag who's here, so this memory turns up when the family searches their name.</span>
      </div>

      {/* Tagged chips + Tag someone */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, alignItems: 'center' }}>
        {(people || []).map(p => (
          <span key={p.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FBFBF3',
            border: '1px solid #E4E1D2', borderRadius: 999, padding: '5px 10px 5px 5px', fontFamily: FONT_B,
          }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: ppAvatarColor(p.name), color: '#fff', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.initial}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#3E4F22' }}>{p.name}</span>
            {p.is_minor && <span style={{ background: '#FBEFD8', color: '#9A6A0A', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>child</span>}
            <button onClick={() => handleUntag(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', borderRadius: '50%', display: 'flex' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F5EAEE'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            ><X size={13} color="#9A3A5B" /></button>
          </span>
        ))}

        {/* Tag someone pill */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => { setPickerOpen(!pickerOpen); setTagQuery(''); }} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none',
            border: '1.5px dashed #C4CBB4', borderRadius: 999, padding: '7px 14px',
            color: '#556B2F', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: FONT_B, minHeight: 38,
          }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F4F7EC'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
          >
            <UserPlus size={14} /> Tag someone
          </button>

          {pickerOpen && <>
            <div onClick={() => setPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 14 }} />
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 280,
              background: '#fff', border: '1px solid #E4E1D2', borderRadius: 14,
              boxShadow: '0 16px 42px rgba(40,46,28,.2)', zIndex: 15, overflow: 'hidden',
            }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #F0EEE3' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={14} color="#9AA088" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                  <input ref={inputRef} value={tagQuery} onChange={e => setTagQuery(e.target.value)}
                    placeholder="Search family & children..."
                    style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 10, border: '1px solid #E4E1D2', fontSize: 13, fontFamily: FONT_B, outline: 'none' }}
                  />
                </div>
              </div>
              <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                {results.map(p => (
                  <div key={p.id} onClick={() => handleTag(p.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', cursor: 'pointer',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ width: 30, height: 30, borderRadius: '50%', background: ppAvatarColor(p.name), color: '#fff', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.initial}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#2C2F26', fontFamily: FONT_B }}>{p.name}</span>
                    {p.is_minor && <span style={{ background: '#FBEFD8', color: '#9A6A0A', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>child</span>}
                  </div>
                ))}
                {results.length === 0 && (
                  <div style={{ padding: '14px', textAlign: 'center', fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>No one left to tag</div>
                )}
              </div>
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Memory Detail Page ─── */
export default function MemoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [notes, setNotes] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [pendingJobId, setPendingJobId] = useState(null);
  const currentUser = getUser() || {};

  const fetchNotes = useCallback(async () => {
    try { setNotes(await getStoryQuestions(id) || []); } catch (err) { console.error(err); }
  }, [id]);

  const fetchEngagement = useCallback(async () => {
    try { setEngagement(await getStoryEngagement(id)); } catch (err) { console.error(err); }
  }, [id]);

  const fetchStory = useCallback(async () => {
    try { setStory(await lifeEventAPI.getEvent(id)); } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchStory(); fetchNotes(); fetchEngagement(); }, [fetchStory, fetchNotes, fetchEngagement]);

  // Fetch pending speaker confirmations for videos
  useEffect(() => {
    if (!story) return;
    const hasVideos = (story.media_files || []).some(m => m.media_type === 'video');
    if (!hasVideos) return;
    getPendingConfirmations()
      .then(data => { if (data.job_ids?.length > 0) setPendingJobId(data.job_ids[0]); })
      .catch(() => {});
  }, [story]);

  if (loading) return <HubLayout activeView="stories"><div style={{ maxWidth: 1240, margin: '0 auto', padding: '30px 40px', textAlign: 'center', color: '#828A76' }}>Loading story...</div></HubLayout>;
  if (!story) return <HubLayout activeView="stories"><div style={{ maxWidth: 1240, margin: '0 auto', padding: '30px 40px', textAlign: 'center' }}><h2 style={{ fontFamily: FONT_H }}>Story not found</h2><button onClick={() => navigate(-1)} style={{ background: '#556B2F', color: '#fff', padding: '10px 20px', borderRadius: 999, border: 'none', cursor: 'pointer', marginTop: 16 }}>Go back</button></div></HubLayout>;

  const isOwner = story.user === currentUser.person_id;
  const authorName = story.author_name || `${story.user_first_name || currentUser.first_name || ''}`.trim() || 'Author';
  const authorInitial = authorName.charAt(0).toUpperCase();
  const category = story.event_data?.story_type || story.story_type || 'family_memory';
  const catConfig = getCategoryConfig(category);
  const dateStr = story.date ? new Date(story.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  // Media comes pre-sorted from the API: originals first, then contributions, each by created_at
  const media = (story.media_files || [])
    .filter(m => m.media_type === 'image' || m.media_type === 'video' || m.media_type === 'audio' || !m.media_type)
    .map(m => ({
      ...m,
      url: m.file_url || (m.id ? getMediaServeUrl(m.id) : ''),
      isOriginal: m.is_original !== undefined ? m.is_original : true,
      contributorName: m.owner ? `${m.owner.first_name || ''} ${m.owner.last_name || ''}`.trim() : null,
      contributorInitial: m.owner ? (m.owner.first_name || '?').charAt(0).toUpperCase() : null,
    }));
  const hasMedia = media.length > 0;
  const hasText = !!story.story;

  const handleRefresh = () => { fetchNotes(); fetchEngagement(); };
  const handleMediaRefresh = () => { fetchStory(); };

  return (
    <HubLayout activeView="stories">
      <div className="md-content" style={{ maxWidth: 1240, margin: '0 auto', padding: '26px 40px 70px' }}>
        {/* Back / Visibility / Edit */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: '#556B2F', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: FONT_B, padding: 0 }}>
            <ArrowLeft size={18} /> Back to Stories
          </button>
          {isOwner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Visibility toggle */}
              <button
                onClick={async () => {
                  const newVis = story.visibility === 'private' ? 'family' : 'private';
                  try {
                    await axiosInstance.patch(`/lifeevent/events/${id}/`, { visibility: newVis, is_private: newVis === 'private' });
                    fetchStory();
                  } catch (e) { console.error('Visibility update failed:', e); }
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: story.visibility === 'private' ? '#FFF3E0' : '#F0F5E8',
                  border: `1px solid ${story.visibility === 'private' ? '#F0C070' : '#C4D89E'}`,
                  color: story.visibility === 'private' ? '#E65100' : '#48611F',
                  fontWeight: 700, fontSize: 13, padding: '8px 14px', borderRadius: 999,
                  cursor: 'pointer', fontFamily: FONT_B,
                }}
              >
                {story.visibility === 'private' ? <><Lock size={14} /> Private</> : <><Globe size={14} /> Family</>}
              </button>
              <button onClick={() => navigate(`/stories/${id}/edit`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #E6E4D8', color: '#5A6150', fontWeight: 700, fontSize: 14, padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT_B }}><Pencil size={14} /> Edit</button>
            </div>
          )}
        </div>

        {/* Masthead (v3: 12a web / 12b mobile) */}
        {(() => {
          const cs = story.child_subject;
          const isChildStory = !!cs;
          const posterDisplay = isChildStory && cs.posterFullName ? cs.posterFullName : authorName;
          const shortDateStr = story.date ? new Date(story.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

          // Child age text
          let childAgeText = '';
          if (isChildStory && cs.childBirthDate) {
            const dob = new Date(cs.childBirthDate);
            const now = new Date();
            let ageYears = now.getFullYear() - dob.getFullYear();
            if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) ageYears--;
            if (ageYears < 1) {
              const months = (now.getFullYear() - dob.getFullYear()) * 12 + now.getMonth() - dob.getMonth();
              childAgeText = `${months} month${months !== 1 ? 's' : ''} old`;
            } else {
              childAgeText = `age ${ageYears}`;
            }
          }

          // Relationship label in parens: (Dad), (Mom), etc.
          const relParens = isChildStory && cs.relationshipLabel ? ` (${cs.relationshipLabel})` : '';

          return (
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              {/* Category pill */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: catConfig.bg, color: catConfig.fg, fontWeight: 700, fontSize: 12, padding: '6px 14px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '1.2px', fontFamily: FONT_B }}>
                <Trophy size={13} /> {catConfig.label}
              </span>

              {/* Title */}
              <h1 className="md-title" style={{
                fontFamily: FONT_H, fontWeight: 700, fontSize: 44, color: '#2C2F26', lineHeight: 1.08,
                margin: '18px 0 14px',
              }}>{story.title || 'Untitled'}</h1>

              {isChildStory ? (
                <>
                  {/* ── Web byline (12a) ── */}
                  <p className="md-desktop-only" style={{
                    fontFamily: FONT_B, fontSize: 16.5, color: '#5A6150',
                    margin: '0 0 10px', lineHeight: 1.5,
                  }}>
                    About <span style={{ fontWeight: 700, color: '#2C2F26' }}>{cs.childFirstName}</span>
                    {childAgeText ? `, ${childAgeText}` : ''}
                    {' · '}
                    Added by <span style={{ fontWeight: 700, color: '#2C2F26' }}>{posterDisplay}</span>
                    {relParens}
                  </p>

                  {/* ── Mobile byline (12b) ── */}
                  <div className="md-mobile-only" style={{
                    fontFamily: FONT_B, fontSize: 15.5, color: '#5A6150',
                    lineHeight: 1.5, marginBottom: 10,
                  }}>
                    <p style={{ margin: '0 0 4px' }}>
                      About <span style={{ fontWeight: 700, color: '#2C2F26' }}>{cs.childFirstName}</span>
                      {childAgeText ? `, ${childAgeText}` : ''}
                    </p>
                    <p style={{ margin: 0 }}>
                      Added by <span style={{ fontWeight: 700, color: '#2C2F26' }}>{posterDisplay}</span>
                      {relParens}
                    </p>
                  </div>

                  {/* ── Web details (12a): no icons ── */}
                  <div className="md-desktop-only-flex" style={{
                    justifyContent: 'center', flexWrap: 'wrap',
                    gap: '6px 12px', fontSize: 15, color: '#828A76', fontFamily: FONT_B,
                  }}>
                    {story.location && <><span>{story.location}</span><span style={{ color: '#C7C9B6' }}>·</span></>}
                    {dateStr && <><span>{dateStr}</span><span style={{ color: '#C7C9B6' }}>·</span></>}
                    <span>Visible to family</span>
                  </div>

                  {/* ── Mobile details (12b) ── */}
                  <div className="md-mobile-only" style={{
                    fontSize: 14, color: '#828A76', fontFamily: FONT_B,
                  }}>
                    {story.location && <>{story.location} · </>}
                    {shortDateStr && <>{shortDateStr} · </>}
                    Family
                  </div>
                </>
              ) : (
                /* ── Non-child byline (unchanged, display name) ── */
                <div style={{
                  display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
                  gap: '6px 12px', fontSize: 15, color: '#828A76', fontFamily: FONT_B,
                }}>
                  <span>Added by <span style={{ fontWeight: 700, color: '#5A6150' }}>{posterDisplay}</span></span>
                  {story.location && <><span style={{ color: '#C7C9B6' }}>·</span><span>{story.location}</span></>}
                  {dateStr && <><span style={{ color: '#C7C9B6' }}>·</span><span>{dateStr}</span></>}
                  <span style={{ color: '#C7C9B6' }}>·</span><span>Visible to family</span>
                </div>
              )}

              {/* Story lede */}
              {hasText && (
                <blockquote style={{ fontFamily: FONT_H, fontSize: 20, fontStyle: 'italic', lineHeight: 1.55, color: '#2C2F26', maxWidth: 680, margin: '20px auto 0', padding: 0, borderLeft: 'none' }}>
                  "{story.story}"
                </blockquote>
              )}
            </div>
          );
        })()}

        {/* People in this memory */}
        <PeopleInMemory storyId={id} people={story.featured_people} onUpdate={handleMediaRefresh} />

        {/* Two-column grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 372px', gap: 40, alignItems: 'start' }} className="md-grid">
          {/* LEFT — Media */}
          <div>
            {/* Gallery header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0 }}>Photos & recordings</h2>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#556B2F', background: '#F0F5E8', padding: '2px 10px', borderRadius: 999 }}>{media.length}</span>
              </div>
              <button onClick={() => setUploaderOpen(!uploaderOpen)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, background: '#556B2F', color: '#fff',
                fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B,
              }}><Plus size={16} /> Add to this memory</button>
            </div>

            {/* Hint + legend */}
            {hasMedia && (
              <p style={{ fontSize: 14, color: '#828A76', marginBottom: 16, fontFamily: FONT_B, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span><Maximize2 size={14} style={{ verticalAlign: '-2px' }} /> Tap any photo to see it full size</span>
                {media.some(m => !m.isOriginal) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6B3FA0', display: 'inline-block' }} />
                    added by family
                  </span>
                )}
              </p>
            )}

            {/* Uploader (collapsed) */}
            {uploaderOpen && <MediaUploader storyId={id} onUploaded={() => { setUploaderOpen(false); handleMediaRefresh(); }} />}

            {/* Story-level processing banner (replaces per-video inline speaker ID) */}
            {(() => {
              const videoIds = media.filter(m => m.media_type === 'video').map(m => m.id);
              if (!videoIds.length) return null;
              return (
                <StoryProcessingBanner
                  videoMediaFileIds={videoIds}
                  jobId={pendingJobId}
                  onStatusChange={(summary) => {
                    if (summary.pending > 0 && !pendingJobId) {
                      getPendingConfirmations()
                        .then(data => { if (data.job_ids?.length > 0) setPendingJobId(data.job_ids[0]); })
                        .catch(() => {});
                    }
                  }}
                />
              );
            })()}

            {/* Media cards — one enclosing card per clip */}
            {hasMedia ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                {media.map((m, idx) => {
                  const isVideo = m.media_type === 'video';
                  const isAudio = m.media_type === 'audio';
                  const isPhoto = !isVideo && !isAudio;
                  const hasContributor = !m.isOriginal && m.contributorName;
                  const DIVIDER = { borderTop: '1px solid #EFEDE2' };

                  return (
                    <div key={m.id || idx} style={{
                      background: '#fff', border: '1px solid #E4E1D2', borderRadius: 18,
                      overflow: 'hidden', boxShadow: '0 12px 30px rgba(40,46,28,.12)',
                    }}>
                      {/* 1. Player — flush to top edges */}
                      {/* Seek to ?t= parameter (deep link from retrieval chunks) */}
                      {isVideo && (
                        <video
                          src={getImgUrl(m)}
                          controls
                          style={{ width: '100%', height: 'auto', display: 'block', maxHeight: idx === 0 ? '70vh' : 'none' }}
                          preload="metadata"
                          ref={el => {
                            if (el && idx === 0) {
                              const params = new URLSearchParams(window.location.search);
                              const seekTo = parseInt(params.get('t'), 10);
                              if (seekTo > 0) {
                                el.onloadedmetadata = () => { el.currentTime = seekTo; };
                              }
                            }
                          }}
                        />
                      )}
                      {isAudio && (
                        <div style={{ background: 'linear-gradient(135deg,#F3EAFA,#EDF2FB)', padding: '24px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                          <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#6B3FA0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Mic size={22} /></div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B }}>Voice note</div>
                            <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B }}>{m.title || 'Audio recording'}</div>
                          </div>
                        </div>
                      )}
                      {isPhoto && (
                        <img
                          src={getImgUrl(m)} alt={m.caption || 'Photo'}
                          onClick={() => setLightboxIdx(idx)}
                          style={{ width: '100%', height: 'auto', display: 'block', maxHeight: idx === 0 ? '70vh' : 'none', objectFit: 'contain', cursor: 'pointer' }}
                          loading="lazy" decoding="async"
                        />
                      )}

                      {/* 2. VideoProcessingBadge (video only) */}
                      {isVideo && (
                        <div className="mc-section" style={{ ...DIVIDER, padding: '8px 16px' }}>
                          <VideoProcessingBadge videoId={m.id} onStatusChange={(newStatus) => {
                            if (newStatus === 'awaiting_confirmation' && !pendingJobId) {
                              getPendingConfirmations()
                                .then(data => { if (data.job_ids?.length > 0) setPendingJobId(data.job_ids[0]); })
                                .catch(() => {});
                            }
                          }} />
                        </div>
                      )}

                      {/* Speaker identification moved to story-level banner above */}

                      {/* 4. Transcript disclosure (video/audio) */}
                      {(isVideo || isAudio) && (
                        <div className="mc-section" style={{ ...DIVIDER, padding: '10px 16px 10px' }}>
                          <VideoTranscript mediaFileId={m.id} />
                        </div>
                      )}

                      {/* 5. Wisdom moments (video only) */}
                      {isVideo && (
                        <div className="mc-section" style={DIVIDER}>
                          <WisdomMomentsList videoId={m.id} personName={authorName} />
                        </div>
                      )}

                      {/* 6. Contributor footer */}
                      {hasContributor && (
                        <div style={{ ...DIVIDER, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%', background: '#6B3FA0',
                            color: '#fff', fontWeight: 700, fontSize: 11,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>{m.contributorInitial}</div>
                          <span style={{ fontSize: 14, color: '#2C2F26', fontFamily: FONT_B }}>
                            <b>{m.contributorName.split(' ')[0]}</b>
                            <span style={{ color: '#828A76' }}> · {isVideo ? 'video memory' : isAudio ? 'voice memory' : 'photo'}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ background: '#fff', border: '2px dashed #D4D0C0', borderRadius: 16, padding: '48px 24px', textAlign: 'center' }}>
                <Camera size={32} color="#828A76" style={{ marginBottom: 12 }} />
                <p style={{ fontSize: 16, color: '#828A76', fontFamily: FONT_B }}>No photos yet. Add some to bring this memory to life.</p>
              </div>
            )}
          </div>

          {/* RIGHT — Sticky conversation rail */}
          <div className="md-rail" style={{ position: 'sticky', top: 24 }}>
            <ConversationRail storyId={id} authorName={authorName} engagement={engagement} notes={notes} onRefresh={handleRefresh} />
          </div>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 1180px) { .md-grid { grid-template-columns: 1fr !important; } .md-rail { position: static !important; } }
        .md-mobile-only { display: none; }
        .md-desktop-only { display: block; }
        .md-desktop-only-flex { display: flex; }
        @media (max-width: 640px) {
          .md-content { padding: 16px 16px 60px !important; }
          .md-grid { gap: 16px !important; }
          .md-mobile-only { display: block !important; }
          .md-desktop-only { display: none !important; }
          .md-desktop-only-flex { display: none !important; }
          .md-title { font-size: 30px !important; line-height: 1.12 !important; text-wrap: balance; }
        }
        .mc-section:empty { display: none; }
      `}</style>

      {lightboxIdx !== null && <Lightbox photos={media} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />}
    </HubLayout>
  );
}
